"use client";

import { ArrowUp } from "lucide-react";
import { useState } from "react";
import Image from "next/image";

import type { Locale } from "./templates/ServicePageTemplate";

const heroT = {
  sv: { pickupAddress: "Upphämtningsadress", enterAddress: "Ange din adress", seePrice: "Se pris" },
  en: { pickupAddress: "Pickup address", enterAddress: "Enter your address", seePrice: "See price" },
};

/* ------------------------------------------------------------------ */
/*  Worker portraits — positioned along a diagonal channel              */
/*  (bottom-left → top-right). x/y are % within the stream container.  */
/* ------------------------------------------------------------------ */

const PP_SIZE = 80;

const CYCLE = 20; // seconds per full journey

const workers = [
  { src: "/workers/worker-7.webp",  x: 48, delay: -18.0 },
  { src: "/workers/worker-8.webp",  x: 62, delay: -16.5 },
  { src: "/workers/worker-15.webp", x: 52, delay: -15.0 },
  { src: "/workers/worker-5.webp",  x: 66, delay: -13.5 },
  { src: "/workers/worker-9.webp",  x: 45, delay: -12.0 },
  { src: "/workers/worker-10.webp", x: 58, delay: -10.5 },
  { src: "/workers/worker-18.webp", x: 68, delay: -9.0 },
  { src: "/workers/worker-6.webp",  x: 50, delay: -7.5 },
  { src: "/workers/worker-11.webp", x: 64, delay: -6.0 },
  { src: "/workers/worker-12.webp", x: 54, delay: -4.5 },
  { src: "/workers/worker-14.webp", x: 60, delay: -3.0 },
  { src: "/workers/worker-16.webp", x: 46, delay: -1.5 },
  { src: "/workers/worker-17.webp", x: 56, delay: 0 },
  { src: "/workers/worker-19.webp", x: 65, delay: 1.5 },
];

/* ------------------------------------------------------------------ */
/*  Balloon portrait — rises diagonally (↑↗) through the channel       */
/* ------------------------------------------------------------------ */

function BalloonPortrait({
  src,
  x,
  delay,
}: {
  src: string;
  x: number;
  delay: number;
}) {
  const id = `bl-${Math.round(x)}-${Math.round(delay * 10)}`;

  return (
    <div
      className="absolute will-change-transform w-14 h-14 sm:w-20 sm:h-20 -translate-x-1/2"
      style={{
        left: `${x}%`,
        bottom: 0,
        animation: `${id} ${CYCLE}s ${delay}s linear infinite both`,
      }}
    >
      <style>{`
        @keyframes ${id} {
          0% {
            transform: translate3d(0, 0, 0) scale(0);
            opacity: 0;
          }
          7% {
            opacity: 1;
            transform: translate3d(${15 * 0.07}vw, -${55 * 0.07}vh, 0) scale(1);
          }
          70% {
            opacity: 1;
          }
          100% {
            transform: translate3d(15vw, -55vh, 0);
            opacity: 0;
          }
        }
      `}</style>
      <div
        className="w-full h-full rounded-full overflow-hidden"
        style={{
          boxShadow: "0 6px 24px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)",
          border: "none",
          animation: `pp-pulse 4s ${(delay * 0.7) % 4}s ease-in-out infinite`,
        }}
      >
        <Image
          src={src}
          alt="Ungdomskraft medarbetare"
          width={PP_SIZE}
          height={PP_SIZE}
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Worker cloud — the floating-portrait stream + top fade-out.        */
/*  Render inside a `relative` parent. Reused by Hero and elsewhere.   */
/* ------------------------------------------------------------------ */

export function WorkerCloud({
  showAddressBar = true,
  bottomClass,
  fadeColor = "white",
}: {
  showAddressBar?: boolean;
  /** Override the launch line of the portrait stream (e.g. "bottom-[100px]"). */
  bottomClass?: string;
  /** Colour the top fade-out blends into — match the page background. */
  fadeColor?: string;
}) {
  const bottom = bottomClass ?? (showAddressBar ? "bottom-[400px] sm:bottom-[415px]" : "bottom-[300px] sm:bottom-[335px]");
  return (
    <>
      {/* Fade-out gradient at the top so portraits don't appear behind header */}
      <div
        className="absolute inset-x-0 top-0 h-[25vh] sm:h-[20vh] z-[6] pointer-events-none"
        style={{ background: `linear-gradient(to bottom, ${fadeColor} 0%, ${fadeColor} 30%, transparent 100%)` }}
        aria-hidden="true"
      />

      {/* Portrait stream — all launch from same bottom line, ~20% horizontal band */}
      <div
        className={`absolute -inset-x-[20%] sm:inset-x-0 h-0 z-[5] pointer-events-none overflow-visible ${bottom}`}
        aria-hidden="true"
      >
        <style>{`
          @keyframes pp-pulse {
            0%, 100% { transform: scale(0.85); }
            50% { transform: scale(1.1); }
          }
        `}</style>
        {workers.map((w, i) => (
          <BalloonPortrait key={i} {...w} />
        ))}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero component                                                     */
/* ------------------------------------------------------------------ */

interface HeroProps {
  headingPrefix?: string;
  headingKeyword?: string;
  headingSuffix?: string;
  subtext?: string;
  heroImage?: string;
  heroImageAlt?: string;
  locale?: Locale;
  showWorkerCloud?: boolean;
  showAddressBar?: boolean;
  addressLabel?: string;
  bookingHref?: string;
}

export default function Hero({
  headingPrefix = "Hjälp med",
  headingKeyword = "bortforsling",
  headingSuffix = "",
  subtext = "Boka bortforsling av möbler, vitvaror och skrymmande föremål - snabbt och enkelt. Vi skickar ungdomar med lastbil direkt till din adress, ofta samma dag. Fast pris, enkel bokning och över 50 000 slutförda uppdrag i Stockholm, Göteborg.",
  heroImage,
  heroImageAlt,
  locale = "sv",
  showWorkerCloud,
  showAddressBar = true,
  addressLabel,
  bookingHref = "/boka",
}: HeroProps = {}) {
  const [address, setAddress] = useState("");
  const l = heroT[locale];

  const isCloud = showWorkerCloud ?? !heroImage;

  return (
    <section className={`relative bg-white ${isCloud ? "pt-[34vh] sm:pt-[60vh] pb-6 sm:pb-10" : "pt-22 pb-2 sm:pt-36 sm:pb-10 overflow-hidden"}`}>

      {isCloud && <WorkerCloud showAddressBar={showAddressBar} />}

      <div className="relative z-10 mx-auto max-w-screen-2xl px-6 xl:px-14">
        <div className={`flex flex-col ${!isCloud ? "lg:flex-row items-end" : ""} gap-8 lg:gap-12`}>
          {/* Text content — always left-aligned */}
          <div className={`${!isCloud ? "flex-1" : ""} min-w-0 ${isCloud ? "max-w-3xl" : ""}`}>

            {/* Heading */}
            <h1
              className={`font-bold tracking-tight text-brand-dark leading-[1.25] ${headingSuffix ? "text-2xl sm:text-4xl lg:text-5xl" : "text-3xl sm:text-5xl lg:text-6xl"}`}
            >
              {headingPrefix} <span className="bg-brand-primary px-3 py-1 rounded-md text-white inline-block">{headingKeyword}</span>
              {headingSuffix && <><br />{headingSuffix}</>}
            </h1>

            {/* Subtext */}
            <p
              className="mt-4 sm:mt-8 text-lg sm:text-xl text-gray-600 max-w-xl"
            >
              {subtext}
            </p>

            {/* Mobile hero image — only for service pages */}
            {!isCloud && heroImage && (
              <div
                className="-mt-4 lg:hidden animate-[hero-fade-up_0.6s_0.2s_ease-out_both]"
              >
                <Image
                  src={heroImage}
                  alt={heroImageAlt ?? "Ungdomskraft"}
                  width={1758}
                  height={700}
                  sizes="100vw"
                  priority
                  className="w-full h-auto object-contain rounded-lg"
                />
              </div>
            )}

            {/* Address input bar */}
            {showAddressBar && (
              <div
                className="mt-5 sm:mt-10 max-w-xl animate-[hero-fade-up_0.4s_0.15s_ease-out_both]"
              >
                <div className="flex items-center rounded-xl border-2 border-brand-secondary bg-white shadow-lg shadow-brand-secondary/10 overflow-hidden focus-within:border-brand-secondary transition-colors">
                  <div className="flex items-center gap-3 flex-1 px-6 py-4">
                    <ArrowUp className="h-5 w-5 text-brand-dark shrink-0" />
                    <div className="flex flex-col flex-1">
                      <span className="text-xs font-medium text-gray-500">{addressLabel ?? l.pickupAddress}</span>
                      <input
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder={l.enterAddress}
                        className="w-full bg-transparent text-brand-dark placeholder:text-gray-400 outline-none text-base"
                      />
                    </div>
                  </div>
                  <a href={bookingHref} className="m-2 rounded-xl bg-brand-secondary px-8 py-3 text-base font-semibold text-white hover:brightness-110 transition-all shrink-0 cursor-pointer inline-flex flex-col items-center">
                    <span>{l.seePrice}</span>
                    <span className="text-sm font-bold text-brand-primary ">50% RUT</span>
                  </a>
                </div>
              </div>
            )}

          </div>

          {/* Right column — hero image (desktop, service pages only) */}
          {!isCloud && heroImage && (
            <div
              className="hidden lg:flex flex-col justify-end lg:w-[55%] shrink-0 min-w-0 relative animate-[hero-fade-right_0.7s_0.2s_ease-out_both]"
            >
              <Image
                src={heroImage}
                alt="Ungdomskraft"
                width={1758}
                height={700}
                sizes="55vw"
                priority
                className="w-full h-auto object-contain rounded-tr-[5px] rounded-br-[5px]"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
